/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useEffect } from "react";
import PropTypes from "prop-types";

import iconEditorReducer from "js/reducers/icon-editor";
import { iconEditorInitialState } from "js/reducers/icon-editor/state";

import {
    getPersistedRecentColorsPalette,
    setInitialColorStatesWithPersistedColors,
    persistUpdatedRecentColorsPalettes,
    fetchEditorPreviewImage,
    fetchToken
} from "./helpers";

import { ICON_EDITOR_PREVIEW_TOKEN } from "core/localStorageKeys";
import { getLocalStorage, setLocalStorage } from "js/helpers/functions";

export const IconEditorStateContext = createContext();
export const IconEditorDispatchContext = createContext();

const IconEditorProvider = ({ iconId, children }) => {
    const {
        persistedIconsColors,
        persistedBackgroundColors
    } = getPersistedRecentColorsPalette();

    const [state, dispatch] = React.useReducer(iconEditorReducer, {
        ...setInitialColorStatesWithPersistedColors({
            state: iconEditorInitialState,
            persistedIconsColors,
            persistedBackgroundColors
        }),
        token: getLocalStorage(ICON_EDITOR_PREVIEW_TOKEN) || null
    });

    useEffect(() => {
        dispatch({
            type: "SET_CURRENT_ICON",
            iconId
        });
    }, []);

    useEffect(() => {
        if (state.currentIconId) {
            dispatch({
                type: "FETCH_PREVIEW_IMAGE"
            });
        }
    }, [state.currentIconId, state.editCount]);

    useEffect(() => {
        if (state.token !== getLocalStorage(ICON_EDITOR_PREVIEW_TOKEN)) {
            setLocalStorage(ICON_EDITOR_PREVIEW_TOKEN, state.token);
        }
    }, [state.token]);

    useEffect(() => {
        if (state.fetchingPreviewImage) {
            const getPreviewImage = async () => {
                if (state.token) {
                    const response = await fetchEditorPreviewImage({
                        state
                    });
                    if (!response.ok) {
                        dispatch({
                            type: "FETCH_PREVIEW_IMAGE_FAILURE",
                            tokenIsExpired: response.status === 401
                        });

                        return;
                    }

                    dispatch({
                        type: "FETCH_PREVIEW_IMAGE_SUCCESS",
                        previewImageHtml: await response.text()
                    });
                } else {
                    dispatch({
                        type: "FETCH_PREVIEW_IMAGE_FAILURE",
                        tokenIsExpired: false
                    });

                    dispatch({
                        type: "FETCH_PREVIEW_IMAGE_TOKEN"
                    });
                }
            };

            getPreviewImage();
        }
    }, [state.fetchingPreviewImage]);

    useEffect(() => {
        if (state.fetchingToken) {
            const getToken = async () => {
                const response = await fetchToken();
                if (!response.ok) {
                    dispatch({
                        type: "FETCH_PREVIEW_IMAGE_TOKEN_FAILURE"
                    });
                }

                const { token } = await response.json();
                dispatch({
                    type: "FETCH_PREVIEW_IMAGE_TOKEN_SUCCESS",
                    token
                });
            };

            getToken();
        }
    }, [state.fetchingToken]);

    const { backgroundColors, iconColors } = state;
    const recentBackgroundColors = backgroundColors.recent;
    const recentIconColors = iconColors.recent;
    const firstRecentBackgroundColor = recentBackgroundColors[0];
    const firstRecentIconColor = recentIconColors[0];

    useEffect(() => {
        persistUpdatedRecentColorsPalettes({
            updatedRecentIconColors: recentIconColors,
            updatedRecentBackgroundColors: recentBackgroundColors
        });
    }, [firstRecentBackgroundColor, firstRecentIconColor]);

    return (
        <IconEditorStateContext.Provider value={state}>
            <IconEditorDispatchContext.Provider value={dispatch}>
                {children}
            </IconEditorDispatchContext.Provider>
        </IconEditorStateContext.Provider>
    );
};

IconEditorProvider.propTypes = {
    iconId: PropTypes.string.isRequired
};

export { IconEditorProvider };

export const useIconEditorState = () => {
    const context = useContext(IconEditorStateContext);

    if (context === undefined) {
        throw new Error(
            "useIconEditorState must be used within an IconEditorProvider"
        );
    }

    return context;
};

export const useIconEditorDispatch = () => {
    const context = useContext(IconEditorDispatchContext);

    if (context === undefined) {
        throw new Error(
            "useIconEditorDispatch must be used within an IconEditorProvider"
        );
    }

    return context;
};
